import type { ApiKey } from '../types/api'
import { formatDate } from './format'
import { secretLabel, type KeyChain } from './keyChains'
import { hasRecordedUse } from './lastUsed'

const HOUR = 60 * 60 * 1000
/** An installation that rotated switches within minutes; the guide tells it to, once the new secret works. */
const SWITCH_GRACE = 24 * HOUR
/** Lets the warning clear once whatever held the old secret stops using it. */
const RECENT = 7 * 24 * HOUR

export interface OldSecretWarning {
  kind: 'stillInUse' | 'newSecretNeverUsed'
  old: ApiKey
  successor: ApiKey
}

const time = (value: string) => new Date(value).getTime()

/** Older working secrets still in use after rotation replaced them. None while tracking is off. */
export function oldSecretWarnings(
  chain: KeyChain,
  trackedSince: string | null | undefined,
  now: Date,
): OldSecretWarning[] {
  if (!trackedSince) return []
  const warnings: OldSecretWarning[] = []
  for (const old of chain.olderWorking) {
    const successor = chain.secrets.find((s) => s.id === old.successor_key_id)
    if (!successor || !old.last_used_at || !hasRecordedUse(old.last_used_at, trackedSince)) continue
    const used = time(old.last_used_at)
    if (used <= time(successor.created_at) + SWITCH_GRACE || used < now.getTime() - RECENT) continue
    const successorUsed = !!successor.last_used_at && hasRecordedUse(successor.last_used_at, trackedSince)
    const successors = chain.secrets.filter((s) => s.rotated_from_key_id === old.id).length
    // With two successors, deleting one would not unblock rotation.
    const neverUsedIsKnown = !successorUsed && time(successor.created_at) >= time(trackedSince) && successors === 1
    warnings.push({ kind: neverUsedIsKnown ? 'newSecretNeverUsed' : 'stillInUse', old, successor })
  }
  return warnings
}

export function describeWarning({ kind, old, successor }: OldSecretWarning): { title: string; body: string } {
  const deadline = formatDate(old.expires_at)
  if (kind === 'newSecretNeverUsed') {
    const fresh = secretLabel(successor)
    return {
      title: 'The new secret has never been used',
      body:
        `The installation still uses ${secretLabel(old)}, which stops working on ${deadline}. ` +
        `It has probably lost the new secret ${fresh}. Delete ${fresh}, and it will rotate again on its own.`,
    }
  }
  return {
    title: 'The previous secret is still in use',
    body:
      `${secretLabel(old)} was last used ${formatDate(old.last_used_at!)}, over a day after it was replaced on ` +
      `${formatDate(successor.created_at)}. Whatever uses it stops working on ${deadline}. ` +
      'If another installation shares this key, give it its own key. ' +
      'Otherwise check that every server of this installation uses the new secret.',
  }
}
